import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generateAiInsight,
  getLatestAiInsight,
  type AiInsight,
} from "../../../api/dashboard";
import { ApiClientError } from "../../../api/types";
import { useAuth } from "../../../context/AuthContext";
import { RoleCode } from "../../../lib/rbac";

interface AiAccountantWidgetProps {
  startDate: string;
  endDate: string;
  refreshKey?: number;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

function MarkdownInsight({ markdown }: { markdown: string }) {
  const lines = markdown.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const bulletLines = lines.filter((line) => /^[-*•]\s+/.test(line.trim()));
  const paragraphLines = lines.filter((line) => !/^[-*•]\s+/.test(line.trim()));

  return (
    <div className="space-y-3 text-sm leading-relaxed text-slate-700">
      {paragraphLines.length > 0 && (
        <div className="space-y-2">
          {paragraphLines.map((line, index) => (
            <p key={`p-${index}`}>{renderInlineMarkdown(line.trim())}</p>
          ))}
        </div>
      )}

      {bulletLines.length > 0 && (
        <ul className="list-disc space-y-2 ps-5 marker:text-godo-accent">
          {bulletLines.map((line, index) => (
            <li key={`li-${index}`}>
              {renderInlineMarkdown(line.trim().replace(/^[-*•]\s+/, ""))}
            </li>
          ))}
        </ul>
      )}

      {lines.length === 0 && (
        <p className="text-slate-500">—</p>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-white"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export function AiAccountantWidget({
  startDate,
  endDate,
  refreshKey = 0,
}: AiAccountantWidgetProps) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canGenerate = hasRole(RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR);

  const [insight, setInsight] = useState<AiInsight | null>(null);
  const [isLoadingInsight, setIsLoadingInsight] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatestInsight = useCallback(async () => {
    setIsLoadingInsight(true);
    setError(null);
    try {
      const latest = await getLatestAiInsight();
      setInsight(latest);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("dashboard.ai_load_failed"),
      );
    } finally {
      setIsLoadingInsight(false);
    }
  }, [t]);

  useEffect(() => {
    void loadLatestInsight();
  }, [loadLatestInsight, refreshKey]);

  async function handleGenerateInsight() {
    if (!canGenerate) {
      setError(t("dashboard.ai_write_forbidden"));
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const generated = await generateAiInsight(startDate, endDate);
      setInsight({
        insightId: generated.insightId,
        orgId: generated.orgId,
        generatedAt: generated.generatedAt,
        contextSnapshotJson: JSON.stringify(generated.contextSnapshot),
        aiResponseMarkdown: generated.aiResponseMarkdown,
        isActioned: 0,
      });
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("dashboard.ai_generate_failed"),
      );
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 shadow-lg">
      <div className="border-b border-white/10 px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3l1.9 5.8H20l-4.8 3.5 1.8 5.7L12 14.8 7 17.9l1.8-5.7L4 8.8h6.1L12 3z"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h3 className="text-base font-semibold text-white">
                {t("dashboard.ai_title")}
              </h3>
            </div>
            <p className="mt-1 text-xs text-emerald-100/80">{t("dashboard.ai_subtitle")}</p>
          </div>

          {insight?.generatedAt && (
            <p className="text-xs text-slate-300">
              {t("dashboard.ai_generated_at")}:{" "}
              <span dir="ltr" className="font-mono text-emerald-100">
                {insight.generatedAt}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4 px-5 py-5 sm:px-6">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-300/40 bg-red-950/40 px-3 py-2 text-sm text-red-100"
          >
            {error}
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-white/95 p-4 shadow-inner sm:p-5">
          {isLoadingInsight && (
            <p className="text-sm text-slate-500">{t("labels.loading")}</p>
          )}

          {!isLoadingInsight && !insight && (
            <p className="text-sm text-slate-500">{t("dashboard.ai_empty")}</p>
          )}

          {!isLoadingInsight && insight && (
            <MarkdownInsight markdown={insight.aiResponseMarkdown} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handleGenerateInsight()}
            disabled={!canGenerate || isGenerating}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-400 px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? (
              <>
                <LoadingSpinner />
                {t("dashboard.ai_generating")}
              </>
            ) : (
              t("dashboard.ai_ask_button")
            )}
          </button>

          <button
            type="button"
            onClick={() => void loadLatestInsight()}
            disabled={isGenerating}
            className="rounded-lg border border-white/20 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("labels.refresh")}
          </button>

          {!canGenerate && (
            <p className="text-xs text-slate-300">{t("dashboard.ai_write_forbidden")}</p>
          )}
        </div>
      </div>
    </article>
  );
}
